/**
 * CDB-TQS API — Postgres Instances (per-client PostgreSQL multi-tenancy).
 *
 * Admin control-plane for `tqs.postgres_instances` +
 * `tqs.client_postgres_instance_mapping` (cdb_tqs #333 / #633). Mirrors the
 * Neo4j `graph-instances` client. All calls go through the BFF `/tqs` admin
 * proxy; cdb_tqs authorizes each request with `require_admin`.
 *
 * NOTE: the cdb_tqs `/postgres-instances` + `/client-postgres-mappings`
 * endpoints are built in a sibling PR (#333 Phase 4). This client targets that
 * agreed contract — see .ai/issues/333-data-isolation-admin-ui-plan.md §4.
 */

import { CDB_TQS_API_BASE_URL, fetchWithLogging } from "./http";

export interface PostgresInstance {
  id: string;
  name: string;
  display_name: string;
  description: string | null;
  // Divergence from graph instances: Postgres connection is stored as
  // components (host/port/database/user), not a single URI string.
  host: string;
  port: number;
  database_name: string;
  username: string;
  is_active: boolean;
  health_status: string; // 'healthy' | 'unhealthy' | 'unknown'
  last_health_check?: string | null;
  // Number of clients mapped to this instance. Delete is blocked when > 0
  // (ON DELETE RESTRICT on the mapping FK).
  client_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface CreatePostgresInstancePayload {
  name: string;
  display_name: string;
  description?: string | null;
  host: string;
  port: number;
  database_name: string;
  username: string;
  password: string; // Fernet-encrypted server-side; never stored/echoed in plaintext
  is_active?: boolean;
}

export interface UpdatePostgresInstancePayload {
  display_name?: string;
  description?: string | null;
  host?: string;
  port?: number;
  database_name?: string;
  username?: string;
  is_active?: boolean;
}

export interface TestPostgresConnectionResult {
  success: boolean;
  message?: string;
  latency_ms?: number;
  postgres_version?: string;
}

/** Ad-hoc connection-test payload — verify credentials before saving a row. */
export interface TestPostgresConnectionPayload {
  host: string;
  port: number;
  database_name: string;
  username: string;
  password: string;
}

/**
 * One row of the client→instance assignment view. A null
 * `postgres_instance_id` means the client has no mapping row and falls back to
 * the default instance (the control DB).
 */
export interface ClientPostgresMapping {
  client_id: string;
  client_name: string;
  postgres_instance_id: string | null;
  instance_display_name: string | null;
  assigned_at?: string | null;
  notes?: string | null;
}

export interface AssignClientPayload {
  postgres_instance_id: string;
  notes?: string | null;
}

export const postgresInstancesApi = {
  // ---- instances -------------------------------------------------------

  /** List all Postgres instances (with per-instance client counts). */
  async getPostgresInstances(): Promise<PostgresInstance[]> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/postgres-instances`,
      { method: "GET" },
    );
    const data = response.data;
    // TQS returns { postgres_instances: [...], total: number }
    if (data?.postgres_instances) return data.postgres_instances as PostgresInstance[];
    if (Array.isArray(data)) return data as PostgresInstance[];
    return [];
  },

  /**
   * Create a new Postgres instance. No trailing slash — a trailing slash
   * triggers a 307 the BFF redirect handler doesn't survive cleanly (→ 502).
   */
  async createPostgresInstance(
    payload: CreatePostgresInstancePayload,
  ): Promise<PostgresInstance> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/postgres-instances`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return response.data as PostgresInstance;
  },

  /** Update non-credential fields (password rotation is a separate call). */
  async updatePostgresInstance(
    id: string,
    payload: UpdatePostgresInstancePayload,
  ): Promise<PostgresInstance> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/postgres-instances/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    return response.data as PostgresInstance;
  },

  /**
   * Rotate the DB password. Kept separate from update so an edit-form misclick
   * cannot accidentally overwrite the stored password.
   */
  async rotatePostgresInstancePassword(id: string, password: string): Promise<PostgresInstance> {
    const response = await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/postgres-instances/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ password }),
    });
    return response.data as PostgresInstance;
  },

  /** Delete an instance. Rejected server-side (400) while clients are mapped to it. */
  async deletePostgresInstance(id: string): Promise<void> {
    await fetchWithLogging(`${CDB_TQS_API_BASE_URL}/postgres-instances/${id}`, {
      method: "DELETE",
    });
  },

  /** Run the connection test against an already-saved instance. */
  async testPostgresInstanceConnectionById(id: string): Promise<TestPostgresConnectionResult> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/postgres-instances/${id}/test-connection`,
      { method: "POST", body: JSON.stringify({}) },
    );
    return (response.data ?? { success: false }) as TestPostgresConnectionResult;
  },

  /** Test arbitrary credentials without persisting anything (pre-save check). */
  async testPostgresConnection(
    payload: TestPostgresConnectionPayload,
  ): Promise<TestPostgresConnectionResult> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/postgres-instances/test-connection`,
      { method: "POST", body: JSON.stringify(payload) },
    );
    return (response.data ?? { success: false }) as TestPostgresConnectionResult;
  },

  // ---- client → instance mapping --------------------------------------

  /** List every client with its current instance (or null = default fallback). */
  async getClientPostgresMappings(): Promise<ClientPostgresMapping[]> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/client-postgres-mappings`,
      { method: "GET" },
    );
    const data = response.data;
    if (data?.mappings) return data.mappings as ClientPostgresMapping[];
    if (Array.isArray(data)) return data as ClientPostgresMapping[];
    return [];
  },

  /** Assign (or move) a client to a Postgres instance — upserts the mapping row. */
  async assignClientPostgresInstance(
    clientId: string,
    payload: AssignClientPayload,
  ): Promise<ClientPostgresMapping> {
    const response = await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/client-postgres-mappings/${clientId}`,
      { method: "PUT", body: JSON.stringify(payload) },
    );
    return response.data as ClientPostgresMapping;
  },

  /** Remove a client's mapping row — reverts it to the default instance. */
  async unassignClientPostgresInstance(clientId: string): Promise<void> {
    await fetchWithLogging(
      `${CDB_TQS_API_BASE_URL}/client-postgres-mappings/${clientId}`,
      { method: "DELETE" },
    );
  },
};
